// @mui material components
import {
  Snackbar,
  Alert,
  Card,
  CircularProgress,
  Backdrop,
  Icon,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
  TextField,
} from "@mui/material";

// Material Dashboard 2 React components
import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import MDInput from "theme/components/MDInput";

// Material Dashboard 2 React example components
import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";

import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios.js";
import DataTable from "theme/Tables/DataTable";
// import MDBadge from "theme/components/MDBadge";
import MDButton from "theme/components/MDButton";
import usePageTitle from "app/pageTitle";

function CustomerPoolViewPage() {
  const pageTitle = "Customer Pool";
  usePageTitle(pageTitle);
  const { ref } = useParams();

  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("");
  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };
  const [openNewProfileDialog, setOpenNewProfileDialog] = useState(false);

  const [refreshPage, setRefreshPage] = useState(false);
  const [customerPool, setCustomerPool] = useState({});
  const [profiles, setProfiles] = useState([]);

  const [openUploadAgentsDialog, setOpenUploadAgentsDialog] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [newProfileIdentifier, setNewProfileIdentifier] = useState(null);

  // const selectionMax = 100;

  async function deleteProfile(reference) {
    setShowLoading(true);
    await axios
      .get(`portal/customers/pools/profiles/${reference}/delete.json`)
      .then((result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);

          setRefreshPage(!refreshPage);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  async function deletePool(reference) {
    setShowLoading(true);

    await axios
      .get(`portal/customers/pools/${reference}/delete.json`)
      .then((result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);

          setRefreshPage(!refreshPage);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  const tableColumns = [
    { Header: "#", accessor: "action", align: "left" },
    { Header: "Identifier", accessor: "identifier", align: "left" },
    { Header: "Added On", accessor: "created", align: "right" },
  ];

  const buildProfileRows = (data) => {
    const rows = [];
    data.map((row) => {
      rows.push({
        identifier: row.customer_identifier,
        action: (
          <MDBox mt={1} variant="button">
            <Tooltip title="Remove">
              <MDButton
                size="small"
                variant="outlined"
                color="error"
                iconOnly
                circular
                onClick={() => {
                  if (window.confirm(`Remove ${row.customer_identifier}?`) === true) {
                    deleteProfile(row.reference);
                  }
                }}
              >
                <Icon sx={{ cursor: "pointer" }} fontSize="small" color="dark">
                  close
                </Icon>
              </MDButton>
            </Tooltip>
          </MDBox>
        ),
        created: row.created,
      });
      return null;
    });

    return rows;
  };

  useEffect(() => {
    const fetchData = async () => {
      setShowLoading(true);
      await axios
        .get(`portal/customers/pools/${ref}/fetch.json`)
        .then((result) => {
          // console.log(result);
          setShowLoading(false);
          if (result.data.status === "success") {
            setCustomerPool(result.data.data.pool);
            const rows = buildProfileRows(result.data.data.profiles);
            setProfiles(rows);
          } else {
            setSeverity("error");
            setErrMsg(result.data.data.message);
            setOpen(true);
          }
        })
        .catch(() => {
          setShowLoading(false);
          setSeverity("error");
          setErrMsg(
            `Unable to complete request. ${
              navigator.onLine ? "" : "Make sure you have an active internet connection"
            }`
          );
          setOpen(true);
        });
    };

    fetchData();
  }, [refreshPage]);

  async function postData(formData) {
    setShowLoading(true);
    await axios
      .post("portal/customers/pools/profiles/upload.json", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then((response) => {
        if (response.data.status === "success") {
          setSeverity("success");
          setErrMsg(response.data.data.message);
          setOpen(true);
          setRefreshPage(!refreshPage);
        } else {
          setSeverity("error");
          setErrMsg(response.data.data.message);
          setOpen(true);
        }
        setShowLoading(false);
      })
      .catch(() => {
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
        setShowLoading(false);
      });
  }

  const uploadProfiles = () => {
    if (!uploadFile) {
      return;
    }
    const formData = new FormData();
    formData.append("reference", ref);
    formData.append("file", uploadFile);

    // console.log(formData)
    setOpenUploadAgentsDialog(false);
    setShowLoading(true);

    postData(formData);
  };

  function fileSelected(e) {
    e.persist();
    const fileTypes = ["xlsx", "xls", "csv"];
    const file = e.target.files[0];
    const extension = file.name.split(".").pop().toLowerCase();
    const supported = fileTypes.indexOf(extension) > -1;
    if (!supported) {
      setSeverity("error");
      setErrMsg("Only excel and csv files accepted.");
      setOpen(true);
      return;
    }
    setUploadFile(e.target.files[0]);
  }

  async function createProfile() {
    if (newProfileIdentifier === null || newProfileIdentifier.length === 0) {
      setSeverity("error");
      setErrMsg(`${customerPool.customer_identifier} is required.`);
      setOpen(true);
      return;
    }

    setOpenNewProfileDialog(false);
    setShowLoading(true);
    const json = {
      identifier: newProfileIdentifier,
      reference: ref,
    };

    await axios
      .post("portal/customers/pools/profiles/create.json", json)
      .then((result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);

          setRefreshPage(!refreshPage);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
        onClick={handleClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>
      <Dialog open={openNewProfileDialog} fullWidth maxWidth="xs">
        <DialogTitle>
          <MDBox>New Profile</MDBox>
        </DialogTitle>
        <DialogContent>
          <MDBox sx={{ marginTop: "15px" }}>
            <MDBox mb={2}>
              <MDInput
                name="phone_number"
                margin="dense"
                label={customerPool.customer_identifier}
                type="text"
                fullWidth
                variant="outlined"
                onChange={(e) => setNewProfileIdentifier(e.target.value)}
              />
            </MDBox>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton
            onClick={() => {
              setOpenNewProfileDialog(false);
            }}
            color="dark"
            variant="outlined"
          >
            Cancel
          </MDButton>
          <MDButton
            color="info"
            variant="outlined"
            onClick={() => {
              createProfile();
            }}
          >
            Add Profile
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog open={openUploadAgentsDialog} fullWidth maxWidth="xs">
        <DialogTitle>
          <MDBox>Upload Profiles</MDBox>
        </DialogTitle>
        <DialogContent>
          <MDBox mt={2} mb={3}>
            <MDBox mt={3}>
              <TextField
                accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                type="file"
                name="upload_file"
                id="upload_file"
                // onChange={handleFileInputChange}
                onChange={(e) => fileSelected(e)}
                fullWidth
              />
            </MDBox>
            <MDBox mt={3}>
              <MDTypography variant="subtitle2" fontSize={14}>
                <b>Important!</b>
                <br />
                <b>
                  <i>{customerPool.customer_identifier_code}</i>
                </b>{" "}
                header must be included in CSV file.
              </MDTypography>
            </MDBox>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton
            onClick={() => {
              setOpenUploadAgentsDialog(false);
            }}
            color="dark"
            variant="outlined"
          >
            Cancel
          </MDButton>
          <MDButton
            color="info"
            variant="outlined"
            onClick={() => {
              uploadProfiles();
            }}
          >
            Upload
          </MDButton>
        </DialogActions>
      </Dialog>
      <MDBox pt={3} pb={3}>
        <MDBox>
          <Card sx={{ height: "100%", marginTop: "0px" }}>
            <MDBox p={3}>
              <MDBox display="flex" justifyContent="space-between" alignItems="center">
                <MDBox>
                  <MDTypography variant="h6" fontWeight="medium">
                    {customerPool.title}
                  </MDTypography>
                  <MDTypography variant="h6" fontWeight="light" fontSize={14}>
                    {customerPool.customer_identifier}
                  </MDTypography>
                </MDBox>
                <MDBox>
                  <Tooltip title="Delete Pool">
                    <MDButton
                      variant="contained"
                      color="error"
                      iconOnly
                      onClick={() => {
                        if (window.confirm("Delete Pool?") === true) {
                          deletePool(ref);
                        }
                      }}
                    >
                      <Icon sx={{ cursor: "pointer" }} fontSize="small" color="dark">
                        delete
                      </Icon>
                    </MDButton>
                  </Tooltip>
                </MDBox>
              </MDBox>
            </MDBox>
          </Card>
        </MDBox>
      </MDBox>

      <MDBox pt={3} pb={3}>
        <MDBox>
          <Card sx={{ height: "100%", marginTop: "0px" }}>
            <MDBox p={3}>
              <MDBox display="flex" justifyContent="space-between" alignItems="center">
                <MDTypography variant="h6" fontWeight="medium">
                  Profiles
                </MDTypography>
                <MDBox>
                  <Tooltip title="Add Profile">
                    <MDButton
                      variant="contained"
                      color="dark"
                      iconOnly
                      onClick={() => {
                        setOpenNewProfileDialog(true);
                      }}
                    >
                      <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                    </MDButton>
                  </Tooltip>
                  &nbsp;
                  <Tooltip title="Upload">
                    <MDButton
                      variant="outlined"
                      color="dark"
                      iconOnly
                      onClick={() => {
                        setOpenUploadAgentsDialog(true);
                      }}
                    >
                      <Icon sx={{ fontWeight: "bold" }}>upload</Icon>
                    </MDButton>
                  </Tooltip>
                </MDBox>
              </MDBox>
              <MDBox mt={5}>
                <DataTable
                  table={{ columns: tableColumns, rows: profiles }}
                  isSorted
                  entriesPerPage
                  showTotalEntries
                  noEndBorder
                />
              </MDBox>
            </MDBox>
          </Card>
        </MDBox>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default CustomerPoolViewPage;
